import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { reasonsForFloor, labelCase, isFloorBranch, DEPARTMENTS, deptLabel } from "@/lib/queueConfig";
import { unlockAudio, playChime } from "@/lib/chime";
import VisitLandingClassic from "@/pages/VisitLandingClassic";
import { useQueueTicket } from "@/lib/useQueueTicket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

// Same warm champagne wash + soft gold corner glows as the in-store TV rate board.
const GLOW = {
  background:
    "radial-gradient(120vh 90vh at 12% -10%, rgba(197,160,89,0.22), transparent 60%)," +
    "radial-gradient(110vh 80vh at 100% 110%, rgba(197,160,89,0.16), transparent 62%)",
};

const INPUT =
  "h-11 rounded-none border-[#E4D9BF] bg-white/80 focus-visible:ring-[#C5A059]";

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7440]">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

/** Public landing page behind the counter QR code — one form that joins the queue
 *  and registers the customer at the same time. No viewing or editing of anyone's
 *  stored details here, so nobody else's information is ever exposed.
 *  Styled to match the in-store TV gold-rate board. */
export default function VisitLanding() {
  const { branch } = useParams();
  const store = (branch || "").toUpperCase();
  const showFloor = isFloorBranch(store);
  const [form, setForm] = useState({
    phone: "",
    name: "",
    address: "",
    email: "",
    department: "",
    services: [],
    note: "",
    party_size: 1,
  });
  // On a floor branch each ticket is for one floor, so only that floor's services are offered.
  const reasons = reasonsForFloor(store, form.department);
  const [privacy, setPrivacy] = useState(false);
  const [joinTicket, setJoinTicket] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Each chime fires once — not on every 10s poll.
  const chimed = useRef({ next: false, called: false });

  // The ticket is remembered on this phone (and polled for a live "people ahead of you"),
  // so a page refresh keeps the customer on their number instead of a blank join form.
  const { ticket, live, setTicket, leaveQueue } = useQueueTicket(store, showFloor);

  // A soft chime so a browsing customer looks up: once when they're next, and a
  // fuller two-note chime the moment staff call them to the counter.
  useEffect(() => {
    if (!live) return;
    if (live.status === "serving" && !chimed.current.called) {
      chimed.current.called = true;
      playChime("called");
    } else if (live.status === "waiting" && live.ahead === 0 && !chimed.current.next) {
      chimed.current.next = true;
      playChime("next");
    }
  }, [live]);

  const fail = (err, fallback) =>
    toast.error(
      (err.response?.data?.detail && String(err.response.data.detail)) || fallback
    );

  const post = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/public/queue`, {
        ...form,
        ...payload,
        branch: store,
      });
      setTicket(data);
    } catch (err) {
      fail(err, "Could not add you to the queue");
    } finally {
      setSaving(false);
    }
  };

  const joinNew = () => {
    unlockAudio();
    if (showFloor && !form.department)
      return toast.error("Please choose Gold, Silver or Gold Bullion so we send you to the right counter");
    if (!form.phone.trim()) return toast.error("Please enter your phone number");
    if (!form.name.trim()) return toast.error("Please enter your full name");
    if (!emailOk(form.email)) return toast.error("Please enter a valid email address");
    if (!privacy)
      return toast.error("Please tick the box to confirm you've read the privacy notice");
    return post({ privacy_accepted: true });
  };

  const joinGroup = () => {
    unlockAudio();
    if (!form.phone.trim() || !form.name.trim())
      return toast.error("Please enter your name and phone number");
    if (!joinTicket.trim()) return toast.error("Enter your group's ticket number");
    return post({ join_ticket: Number(joinTicket) });
  };

  const toggleService = (r) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(r)
        ? f.services.filter((x) => x !== r)
        : [...f.services, r],
    }));

  const liveClosed = live && ["done", "merged", "no_show"].includes(live.status);

  // Only floor branches (Truganina) use this new multi-service page. Dandenong keeps the
  // classic v1.0.22 join page unchanged.
  if (!showFloor) return <VisitLandingClassic />;

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#FBF7EF] px-4 py-10 text-[#2A2318]"
      data-testid="visit-landing"
    >
      <div className="pointer-events-none absolute inset-0" style={GLOW} />

      <div className="relative mx-auto max-w-md">
        {/* Branded header — logo, serif wordmark and gold rule, exactly like the TV board */}
        <div className="flex flex-col items-center text-center">
          <img
            src="/brand/logo.png"
            alt="Dhunna Jewellers"
            className="h-16 w-auto object-contain"
            style={{ mixBlendMode: "multiply" }}
            data-testid="visit-logo"
          />
          <p className="font-invoice mt-3 text-2xl font-bold uppercase tracking-[0.18em] text-[#2A2318]">
            Dhunna Jewellers
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.34em] text-[#8A7440]">
            {store} store
          </p>
        </div>
        <div className="mx-auto mt-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#C5A059]" />
          <span className="font-invoice text-xs uppercase tracking-[0.2em] text-[#B08D45]">
            {ticket ? "Your Place In Line" : "Join The Queue"}
          </span>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#C5A059]" />
        </div>

        <div className="mt-6 border border-[#E4D9BF] bg-white/75 p-6 shadow-[0_1px_0_rgba(197,160,89,0.25)] sm:p-8">
          {ticket ? (
            <div className="py-4 text-center" data-testid="queue-ticket">
              <CheckCircle2 className="mx-auto h-11 w-11 text-[#9A7822]" />
              <p className="mt-3 text-[11px] uppercase tracking-[0.24em] text-[#8A7440]">
                {liveClosed ? "Your ticket" : "You're in the queue — ticket"}
              </p>
              <p
                className="tabular font-invoice mt-1 text-7xl font-bold text-[#2A2318]"
                data-testid="queue-ticket-number"
              >
                {ticket.ticket_label || ticket.ticket}
              </p>
              {ticket.department && (
                <p
                  className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#B08D45]"
                  data-testid="queue-ticket-dept"
                >
                  {deptLabel(ticket.department)}
                </p>
              )}
              {(ticket.services || []).length > 0 && (
                <div
                  className="mt-3 flex flex-wrap justify-center gap-1.5"
                  data-testid="queue-ticket-services"
                >
                  {(ticket.services || []).map((s, i) => (
                    <span
                      key={i}
                      className="border border-[#E4D9BF] bg-[#FBF7EF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#8A7440]"
                    >
                      {labelCase(s.reason)}
                    </span>
                  ))}
                </div>
              )}

              {live && ["done", "merged"].includes(live.status) ? (
                <div
                  className="mt-6 border border-[#9A7822]/40 bg-[#F4EFE0] px-4 py-5"
                  data-testid="queue-live-done"
                >
                  <p className="font-invoice text-xl font-bold text-[#9A7822]">
                    Thanks — you've been served
                  </p>
                  <p className="mt-1 text-sm text-[#6F6349]">We hope to see you again soon.</p>
                </div>
              ) : live && live.status === "no_show" ? (
                <div
                  className="mt-6 border border-[#B24A3A]/40 bg-[#FBEDE8] px-4 py-5"
                  data-testid="queue-live-noshow"
                >
                  <p className="font-invoice text-xl font-bold text-[#B24A3A]">We missed you</p>
                  <p className="mt-1 text-sm text-[#6F6349]">
                    Please see our staff and we'll get you seen to.
                  </p>
                </div>
              ) : live && (live.status === "serving" || live.ahead === 0) ? (
                <div
                  className="mt-6 border border-[#C5A059] bg-[#F4EFE0] px-4 py-6 shadow-[0_1px_0_rgba(197,160,89,0.3)]"
                  data-testid="queue-live-turn"
                >
                  <p className="font-invoice text-2xl font-bold text-[#9A7822]">
                    {live.status === "serving" ? "It's your turn" : "You're next"}
                  </p>
                  <p className="mt-1 text-sm text-[#6F6349]">
                    {ticket.department
                      ? `Please come to the ${deptLabel(ticket.department)} counter.`
                      : "Please come to the counter."}
                  </p>
                </div>
              ) : (
                <div
                  className="mt-6 border border-[#E4D9BF] bg-[#FBF7EF] px-4 py-6"
                  data-testid="queue-live-ahead"
                >
                  <p className="tabular font-invoice text-5xl font-bold text-[#2A2318]">
                    {live ? live.ahead : "—"}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-[0.22em] text-[#8A7440]">
                    {live && live.ahead === 1 ? "person ahead of you" : "people ahead of you"}
                    {ticket.department ? " at your counter" : ""}
                  </p>
                  {live && (live.now_serving_labels || live.now_serving || []).length > 0 && (
                    <p className="mt-3 text-sm text-[#6F6349]" data-testid="queue-live-serving">
                      Now serving {(live.now_serving_labels || live.now_serving || []).join(", ")}
                    </p>
                  )}
                  {live && live.est_wait_min > 0 && (
                    <p className="mt-1 text-sm text-[#6F6349]" data-testid="queue-live-wait">
                      Estimated wait: about {live.est_wait_min} min
                    </p>
                  )}
                </div>
              )}

              {!liveClosed && (
                <p className="mt-4 text-sm text-[#6F6349]">
                  Feel free to browse while you wait — this page updates on its own.
                </p>
              )}
              {(ticket.party_size || 1) > 1 && (
                <p
                  className="mt-2 text-xs uppercase tracking-wider text-[#B08D45]"
                  data-testid="queue-ticket-party"
                >
                  One ticket for your party of {ticket.party_size}
                </p>
              )}
              {confirmLeave ? (
                <div className="mt-6 flex flex-col items-center gap-2" data-testid="queue-leave-confirm-row">
                  <p className="text-xs uppercase tracking-wider text-[#8A6C36]">
                    Leave and give up your spot?
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={async () => {
                        await leaveQueue();
                        setConfirmLeave(false);
                        toast.success("You've left the queue");
                      }}
                      className="border border-[#B24A3A]/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#B24A3A] hover:bg-[#FBEDE8]"
                      data-testid="queue-leave-confirm"
                    >
                      Yes, leave
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmLeave(false)}
                      className="border border-[#B08D45]/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#8A6C36] hover:bg-[#F4EFE0]"
                      data-testid="queue-leave-cancel"
                    >
                      Keep my spot
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmLeave(true)}
                  className="mt-6 text-xs uppercase tracking-wider text-[#B08D45] underline-offset-2 hover:underline"
                  data-testid="queue-leave"
                >
                  Leave the queue
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4" data-testid="queue-form">
              <p className="text-sm text-[#6F6349]">
                Join the queue and we'll call you by number. Your details are saved so we
                don't have to ask again next time.
              </p>
              {showFloor && (
                <div data-testid="queue-dept-step">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7440]">
                    Which are you here for? *
                  </Label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {DEPARTMENTS.map((d) => (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            department: d.key,
                            services: f.services.filter((s) =>
                              reasonsForFloor(store, d.key).includes(s)
                            ),
                          }))
                        }
                        className={`border px-3 py-3 text-left transition-colors duration-150 ${
                          form.department === d.key
                            ? "border-[#C5A059] bg-[#F4EFE0]"
                            : "border-[#E4D9BF] bg-white/60 hover:border-[#C5A059]/60"
                        }`}
                        data-testid={`queue-dept-${d.key}`}
                      >
                        <span className="block text-sm font-semibold">{d.label}</span>
                        <span className="block text-xs text-[#8A7440]">{d.floor}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Field label="Phone number *">
                <Input
                  className={INPUT}
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="queue-phone-input"
                />
              </Field>
              <Field label="Full name *">
                <Input
                  className={INPUT}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="queue-name-input"
                />
              </Field>
              <Field label="Address (optional)">
                <AddressAutocomplete
                  className={INPUT}
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  placeholder="Start typing your address"
                  testid="queue-address-input"
                />
              </Field>
              <Field label="Email address *">
                <Input
                  className={INPUT}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="queue-email-input"
                />
              </Field>

              {(!showFloor || form.department) && (
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7440]">
                  What can we help with? (tick all that apply)
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {reasons.map((r) => {
                    const on = form.services.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => toggleService(r)}
                        className={`relative border px-3 py-2.5 text-sm transition-colors duration-150 ${
                          on
                            ? "border-[#C5A059] bg-[#F4EFE0] font-semibold"
                            : "border-[#E4D9BF] bg-white/60 hover:border-[#C5A059]/60"
                        }`}
                        data-testid={`queue-reason-${r.replace(/[^A-Za-z0-9]+/g, "-")}`}
                      >
                        {on && (
                          <CheckCircle2 className="absolute right-1.5 top-1.5 h-3.5 w-3.5 text-[#9A7822]" />
                        )}
                        {labelCase(r)}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              <Field label="Anything we should know? (optional)">
                <Input
                  className={INPUT}
                  placeholder="e.g. ring resize, size 7"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  data-testid="queue-note-input"
                />
              </Field>

              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7440]">
                  How many people are with you?
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, party_size: n })}
                      className={`h-11 w-12 border text-sm transition-colors duration-150 ${
                        form.party_size === n
                          ? "border-[#C5A059] bg-[#F4EFE0] font-semibold"
                          : "border-[#E4D9BF] bg-white/60 hover:border-[#C5A059]/60"
                      }`}
                      data-testid={`queue-party-${n}`}
                    >
                      {n === 5 ? "5+" : n}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-[#8A7440]">
                  One ticket covers your whole group — no need for everyone to scan.
                </p>
              </div>

              <PrivacyNotice
                branch={branch}
                accepted={privacy}
                onChange={setPrivacy}
                testid="queue-privacy"
              />
              <Button
                className="w-full rounded-none bg-[#C5A059] py-6 font-semibold text-[#2A2318] hover:bg-[#B38F4E]"
                onClick={joinNew}
                disabled={saving}
                data-testid="queue-submit-button"
              >
                {saving ? "Adding you…" : "Join the queue"}
              </Button>

              <div className="border-t border-[#E4D9BF] pt-4">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7440]">
                  Someone in my group already joined
                </Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    className={INPUT}
                    placeholder="Their ticket number"
                    inputMode="numeric"
                    value={joinTicket}
                    onChange={(e) => setJoinTicket(e.target.value)}
                    data-testid="queue-join-ticket-input"
                  />
                  <Button
                    variant="outline"
                    className="h-11 whitespace-nowrap rounded-none border-[#C5A059] text-[#9A7822] hover:bg-[#F4EFE0]"
                    onClick={joinGroup}
                    disabled={saving}
                    data-testid="queue-join-ticket-button"
                  >
                    Join them
                  </Button>
                </div>
                <p className="mt-1.5 text-xs text-[#8A7440]">
                  Just enter your name and phone above, then their ticket number.
                </p>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-[#6F6349]/50">
          Dhunna Jewellers · {store}
        </p>
      </div>
    </div>
  );
}
